import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Play, CheckCircle, XCircle, Clock, Calendar,
         AlertTriangle, ChevronDown, ChevronUp, Inbox } from 'lucide-react';
import api from '../../api/axios';
import { useToast } from '../Toast';

const STATUS_MAP = {
  sent:       { label: 'Envoyé',          cls: 'bg-blue-100 text-blue-600'    },
  failed:     { label: 'Échec',           cls: 'bg-red-100 text-red-600'      },
};
const CONF_MAP = {
  pending:    { label: 'En attente',      cls: 'bg-slate-100 text-slate-500', icon: Clock         },
  confirmed:  { label: 'Confirmé',        cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  declined:   { label: 'Refusé',          cls: 'bg-red-100 text-red-600',     icon: XCircle       },
  rescheduled:{ label: 'Autre date proposée', cls: 'bg-amber-100 text-amber-700', icon: Calendar  },
};

function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}
function fmtDateTime(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function ConfBadge({ status }) {
  const cfg = CONF_MAP[status] || CONF_MAP.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>
      <Icon size={10} /> {cfg.label}
    </span>
  );
}

function ActionRequise({ log, onRespond }) {
  const [date, setDate]       = useState('');
  const [mode, setMode]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const showToast = useToast();

  const submit = async () => {
    if (mode === 'counter' && !date) return showToast('Choisissez une date à proposer', 'error');
    setSaving(true);
    try {
      await api.post(`/rappels/${log._id}/admin-response`, {
        action: mode === 'accept' ? 'accept' : 'counter_propose',
        proposedDate: mode === 'counter' ? date : undefined,
      });
      showToast(mode === 'accept' ? 'Date acceptée - le client est notifié' : 'Contre-proposition envoyée au client', 'success');
      onRespond();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <AlertTriangle size={14} className="text-amber-500" />
            <p className="text-sm font-bold text-amber-800">Action requise</p>
          </div>
          <p className="text-sm text-slate-700 font-semibold">
            {log.clientId?.prenom} {log.clientId?.nom}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Propose le <strong>{fmtDate(log.clientProposedDate)}{log.clientPreferredTime ? ` à ${log.clientPreferredTime}` : ''}</strong>
            <span className="mx-1.5 text-slate-300">·</span>
            Rappel envoyé le {fmtDateTime(log.createdAt)}
          </p>
        </div>
      </div>

      {!mode ? (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setMode('accept')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700 transition">
            <CheckCircle size={12} /> Accepter cette date
          </button>
          <button onClick={() => setMode('counter')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 transition">
            <Calendar size={12} /> Proposer une autre date
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {mode === 'counter' && (
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 text-slate-900" />
          )}
          <div className="flex gap-2">
            <button onClick={submit} disabled={saving}
              className="flex-1 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition">
              {saving ? 'Envoi…' : mode === 'accept' ? 'Confirmer l\'acceptation' : 'Envoyer la contre-proposition'}
            </button>
            <button onClick={() => setMode(null)}
              className="px-3 py-2 text-xs text-slate-500 rounded-xl border border-slate-200 hover:bg-slate-50 transition">
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LogRow({ log }) {
  const [open, setOpen] = useState(false);
  const conf = CONF_MAP[log.confirmationStatus] || CONF_MAP.pending;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.statut === 'sent' ? 'bg-blue-500' : 'bg-red-400'}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {log.clientId?.prenom} {log.clientId?.nom}
              <span className="ml-2 text-xs font-normal text-slate-400">{log.type === 'auto' ? 'Automatique' : 'Manuel'}</span>
            </p>
            <p className="text-xs text-slate-400">{fmtDateTime(log.createdAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <ConfBadge status={log.confirmationStatus} />
          {open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 bg-slate-50/50 space-y-2 text-xs text-slate-500">
          <p><span className="font-medium text-slate-700">Email :</span> {log.email}</p>
          <p><span className="font-medium text-slate-700">Objet :</span> {log.sujet}</p>
          {log.clientRespondedAt && (
            <p><span className="font-medium text-slate-700">Réponse client :</span> {fmtDateTime(log.clientRespondedAt)}</p>
          )}
          {log.clientProposedDate && (
            <p><span className="font-medium text-slate-700">Date proposée par le client :</span> {fmtDate(log.clientProposedDate)}{log.clientPreferredTime ? ` à ${log.clientPreferredTime}` : ''}</p>
          )}
          {log.clientPreferredTime && !log.clientProposedDate && (
            <p><span className="font-medium text-slate-700">Heure souhaitée :</span> {log.clientPreferredTime}{log.clientEstimatedDuration ? ` (durée ~${log.clientEstimatedDuration} min)` : ''}</p>
          )}
          {log.adminResponseStatus && (
            <p><span className="font-medium text-slate-700">Réponse admin :</span> {log.adminResponseStatus === 'accepted' ? 'Accepté' : 'Contre-proposition'}</p>
          )}
          {log.adminProposedDate && (
            <p><span className="font-medium text-slate-700">Date proposée par le salon :</span> {fmtDate(log.adminProposedDate)}</p>
          )}
          {log.bookingId && (
            <p className="text-emerald-600 font-semibold">
              ✅ RDV créé automatiquement dans le planning - employé à assigner
            </p>
          )}
          {log.errorMessage && (
            <p className="text-red-400"><span className="font-medium">Erreur :</span> {log.errorMessage}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function RappelSuivi() {
  const showToast = useToast();
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [triggering,setTriggering]= useState(false);
  const [tab,       setTab]       = useState('actions');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/rappels/logs');
      setLogs(data.data || []);
    } catch { showToast('Erreur chargement des rappels', 'error'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const { data } = await api.post('/rappels/trigger');
      showToast(`Campagne lancée - ${data.sent} rappel(s) envoyé(s)`, 'success');
      load();
    } catch { showToast('Erreur lors du déclenchement', 'error'); }
    finally { setTriggering(false); }
  };

  const pending = logs.filter(l => l.confirmationStatus === 'rescheduled' && !l.adminResponseStatus);
  const history = logs;

  return (
    <div className="space-y-5">
      {/* Header actions */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h3 className="font-bold text-slate-800">Suivi des rappels</h3>
          <p className="text-xs text-slate-400 mt-0.5">Gérez les réponses clients et l'historique complet des rappels</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load}
            className="flex items-center gap-2 px-3.5 py-2 text-sm rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition font-medium">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button onClick={handleTrigger} disabled={triggering}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-50 transition">
            <Play size={13} />
            {triggering ? 'Lancement…' : 'Lancer les rappels maintenant'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        {[
          { key: 'actions',     label: `Actions requises${pending.length ? ` (${pending.length})` : ''}` },
          { key: 'historique',  label: 'Historique' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm rounded-lg font-semibold transition-all ${
              tab === t.key
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : tab === 'actions' ? (
        pending.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
            <Inbox size={36} className="mx-auto mb-3 text-slate-200" />
            <p className="text-slate-400 font-medium">Aucune action requise</p>
            <p className="text-xs text-slate-300 mt-1">Vous serez notifié ici quand un client propose une autre date</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map(log => <ActionRequise key={log._id} log={log} onRespond={load} />)}
          </div>
        )
      ) : (
        history.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
            <Inbox size={36} className="mx-auto mb-3 text-slate-200" />
            <p className="text-slate-400 font-medium">Aucun rappel envoyé pour l'instant</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map(log => <LogRow key={log._id} log={log} />)}
          </div>
        )
      )}
    </div>
  );
}
